import {
  type ZoneLapData,
  type InconsistencyType,
} from '../zones/TrackZoneTypes'

/**
 * Get the value to sort laps by based on the primary issue type.
 * This ensures we show laps that demonstrate the actual variance.
 */
export function getLapSortValue(
  lap: ZoneLapData,
  issueType: InconsistencyType | undefined
): number {
  switch (issueType) {
    case 'entry-speed':
      return lap.entrySpeed
    case 'exit-speed':
      return lap.exitSpeed
    case 'apex-speed':
      return lap.minSpeed
    case 'braking-intensity':
      return lap.peakBrakingG ?? 0
    case 'apex-location':
      return lap.apexDistance ?? 0
    case 'braking-point':
    case 'throttle-point':
      return lap.entryDistance
    default:
      return lap.zoneTimeMs
  }
}

/**
 * Get the 5 most insightful laps for a zone based on the primary issue.
 *
 * Always includes the fastest and slowest laps through the zone (by zone time),
 * then fills up to 3 remaining slots with evenly-spaced laps sorted by the
 * issue-relevant metric to show the range of variance.
 */
export function getKeyLapIds(
  lapData: ZoneLapData[],
  issueType?: InconsistencyType,
  setAsideLapIds?: Set<string>
): Set<string> {
  const eligible =
    setAsideLapIds && setAsideLapIds.size > 0
      ? lapData.filter((l) => !setAsideLapIds.has(l.lapId))
      : lapData

  if (eligible.length <= 5) {
    return new Set(eligible.map((l) => l.lapId))
  }

  const keyLapIds = new Set<string>()

  const sortedByTime = [...eligible].sort((a, b) => a.zoneTimeMs - b.zoneTimeMs)
  keyLapIds.add(sortedByTime[0].lapId)
  keyLapIds.add(sortedByTime[sortedByTime.length - 1].lapId)

  const sorted = [...eligible].sort(
    (a, b) => getLapSortValue(a, issueType) - getLapSortValue(b, issueType)
  )
  const remaining = sorted.filter((l) => !keyLapIds.has(l.lapId))

  if (remaining.length > 0) {
    const slotsLeft = 5 - keyLapIds.size
    const step = remaining.length / (slotsLeft + 1)
    for (let i = 1; i <= slotsLeft && keyLapIds.size < 5; i++) {
      const idx = Math.min(Math.floor(i * step), remaining.length - 1)
      keyLapIds.add(remaining[idx].lapId)
    }
  }

  return keyLapIds
}

/**
 * Get the most insightful laps for display, sorted by zone time.
 * Returns up to 5 laps: fastest, slowest, and evenly-spaced laps
 * by the issue-relevant metric.
 */
export function getDisplayLaps(
  lapData: ZoneLapData[],
  primaryIssue: InconsistencyType | null | undefined,
  setAsideLapIds?: Set<string>
): ZoneLapData[] {
  // Selecting via getKeyLapIds keeps table rows and map markers (which use
  // getKeyLapIds directly) guaranteed to agree on which laps are shown.
  const keyLapIds = getKeyLapIds(
    lapData,
    primaryIssue ?? undefined,
    setAsideLapIds
  )
  return lapData
    .filter((lap) => keyLapIds.has(lap.lapId))
    .sort((a, b) => a.zoneTimeMs - b.zoneTimeMs)
}
